import {WmWoundDocumentation} from '../../interfaces/wound-management/wm-wound-documentation';
import {WmWoundDocumentationEntryFactory} from './wm-wound-documentation-entry-factory';
import {WmWoundDocumentationEntry} from '../../interfaces/wound-management/wm-wound-documentation-entry';
import {WmWoundLocation} from '../../interfaces/wound-management/wm-wound-location';
import {WmWoundLocationFactory} from './wm-wound-location-factory';
import {WmWoundQuestion} from '../../interfaces/wound-management/wm-wound-question';
import {WmWoundQuestionFactory} from './wm-wound-question-factory';
import {WmWoundFinishedReason} from '../../interfaces/wound-management/wm-wound-finished-reason';
import {WmWoundFinishedReasonFactory} from './wm-wound-finished-reason-factory';
import {LightEmployee} from '../../interfaces/employees/light-employee';
import {LightEmployeeFactory} from '../employees/light-employee-factory';
import {LightCustomer} from '../../interfaces/customers/light-customer';
import {LightCustomerFactory} from '../customers/light-customer-factory';
import {Customer} from "../../interfaces/customers/customer";
import {DateTime} from "luxon";
import {UserService} from "../../services/core/user.service";

export class WmWoundDocumentationFactory {

  /**
   *
   * @param wmWoundDocumentation
   */
  public static fromObject(wmWoundDocumentation: any): WmWoundDocumentation {
    return new class implements WmWoundDocumentation {
      comment: string = wmWoundDocumentation.comment;
      createdDate: string = wmWoundDocumentation.created_date;
      customerId: number = wmWoundDocumentation.customer_id;
      diagnosis: string = wmWoundDocumentation.diagnosis;
      finished: string = wmWoundDocumentation.finished;
      wmWoundDocumentationEntriesCount: number = wmWoundDocumentation.wm_wound_documentation_entries_count;
      finishedEmployeeId: number = wmWoundDocumentation.finished_employee_id;
      finishedBy: LightEmployee =
        wmWoundDocumentation.finished_by ?
          LightEmployeeFactory.fromObject(wmWoundDocumentation.finished_by) : null;
      primaryNurseEmployeeId: number = wmWoundDocumentation.primary_nurse_employee_id;
      primaryNurse: LightEmployee = wmWoundDocumentation.primary_nurse ?
        LightEmployeeFactory.fromObject(wmWoundDocumentation.primary_nurse) : null;
      inProgress: boolean = wmWoundDocumentation.in_progress;
      localisationDetails: string = wmWoundDocumentation.localisation_details;
      wmWoundLocation: WmWoundLocation = WmWoundLocationFactory.getObject(wmWoundDocumentation.wm_wound_location);
      userId: number = wmWoundDocumentation.user_id;
      wmWoundFinishedReasonId: number = wmWoundDocumentation.wm_wound_finished_reason_id;
      wmWoundFinishedReason: WmWoundFinishedReason =
        wmWoundDocumentation.wm_wound_finished_reason ?
          WmWoundFinishedReasonFactory.fromObject(wmWoundDocumentation.wm_wound_finished_reason) : null;
      wmWoundQuestions: WmWoundQuestion[] =
        wmWoundDocumentation.wm_wound_questions ?
          WmWoundQuestionFactory.fromArray(wmWoundDocumentation.wm_wound_questions) : [];
      wmWoundLocationId: number = wmWoundDocumentation.wm_wound_location_id;
      wmWoundDocumentationEntries: WmWoundDocumentationEntry[] = wmWoundDocumentation.wm_wound_documentation_entries ?
        WmWoundDocumentationEntryFactory.fromArray(wmWoundDocumentation.wm_wound_documentation_entries) : [];
      customer: LightCustomer = LightCustomerFactory.fromCustomer(wmWoundDocumentation.customer);
      woundFinishedReason: string = wmWoundDocumentation.wound_finished_reason;

      // ------------------------------------------------------------------------------------------------
      id: number = wmWoundDocumentation.id;
      uuid: string = wmWoundDocumentation.uuid;
      modified: string = wmWoundDocumentation.modified;
      created: string = wmWoundDocumentation.created;
      deleted: 0 | 1 = 0;
    };


  }

  /**
   *
   * @param wmWoundDocumentations
   */
  public static fromArray(wmWoundDocumentations: any[]): WmWoundDocumentation[] {
    return wmWoundDocumentations.map(_wmWoundDocumentation => {
      return WmWoundDocumentationFactory.fromObject(_wmWoundDocumentation);
    });
  }

  /**
   *
   * @param customer
   */
  public static async new(customer: LightCustomer): Promise<WmWoundDocumentation> {
    return new class implements WmWoundDocumentation {
      comment: string = null;
      createdDate: string = DateTime.local().toISODate();
      customer: LightCustomer = customer;
      customerId: number = customer.id;
      diagnosis: string = null;
      finished: string = null;
      finishedBy: LightEmployee = null;
      finishedEmployeeId: number = null;
      inProgress: boolean = true;
      localisationDetails: string = null;
      primaryNurse: LightEmployee = UserService.getUser().employee;
      primaryNurseEmployeeId: number = UserService.getUser().employee.id;
      userId: number = UserService.getUser().id;
      wmWoundDocumentationEntries: WmWoundDocumentationEntry[] = [];
      wmWoundDocumentationEntriesCount: number = 1;
      wmWoundFinishedReason: WmWoundFinishedReason = null;
      wmWoundFinishedReasonId: number = null;
      wmWoundLocation: WmWoundLocation = null;
      wmWoundLocationId: number = null;
      wmWoundQuestions: WmWoundQuestion[] = [];
      woundFinishedReason: string = null;

      // ------------------------------------------------------------------------------------------------
      id: number = null;
      uuid: string = null;
      modified: string = null;
      created: string = null;
      deleted: 0 | 1 = 0;
    };
  }

  /**
   *
   * @param wmWoundDocumentation
   */
  public static toRemote(wmWoundDocumentation: WmWoundDocumentation): any {
    return {
      id: wmWoundDocumentation.id,
      comment: wmWoundDocumentation.comment,
      created_date: wmWoundDocumentation.createdDate,
      customer_id: wmWoundDocumentation.customerId,
      diagnosis: wmWoundDocumentation.diagnosis,
      localisation_details: wmWoundDocumentation.localisationDetails,
      wm_wound_location_id: wmWoundDocumentation.wmWoundLocationId,
      uuid: wmWoundDocumentation.uuid,
      modified: wmWoundDocumentation.modified,
      created: wmWoundDocumentation.created,
      wm_wound_finished_reason_id: wmWoundDocumentation.wmWoundFinishedReasonId,
      finished: wmWoundDocumentation.finished,
      finished_employee_id: wmWoundDocumentation.finishedEmployeeId,
      wound_finished_reason: wmWoundDocumentation.woundFinishedReason
    };
  }

}
